/* eslint-disable no-nested-ternary */
import React, { memo, useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { useGridCellEditor } from 'ag-grid-react'
import type { CustomCellEditorProps } from 'ag-grid-react'
import { useTranslation } from 'react-i18next'
import { getLocaleSettings, getLocaleFromLanguage } from '../gridFormatUtils'
import './NumericCellEditor.css'

interface NumericCellEditorParams {
  allowNegative?: boolean
  allowDecimals?: boolean
  maxValue?: number
  minValue?: number
  placeholder?: string
}

const NumericCellEditor = memo(
  ({ value, onValueChange, stopEditing, column, eventKey, cellStartedEdit }: CustomCellEditorProps<any, number>) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const [internalValue, setInternalValue] = useState<string>('')
    const [hasError, setHasError] = useState(false)
    const { i18n } = useTranslation()

    // Get custom params from column definition
    const params = (column?.getColDef()?.cellEditorParams || {}) as NumericCellEditorParams
    const { allowNegative = true, allowDecimals = true, maxValue, minValue, placeholder = '' } = params

    // Get locale-specific settings
    const localeSettings = useMemo(() => {
      const locale = getLocaleFromLanguage(i18n.language)
      return getLocaleSettings(locale)
    }, [i18n.language])

    const { decimalSeparator } = localeSettings

    // Initialize the editor value
    useEffect(() => {
      let startValue: string
      let shouldSelectAll = false

      // Handle backspace key
      if (eventKey === 'Backspace' || eventKey === 'Delete') {
        startValue = ''
        // Immediately notify of null value
        onValueChange(null)
      }
      // Handle numeric key press or decimal separator
      else if (eventKey && new RegExp(`^[0-9\\-${decimalSeparator.replace('.', '\\.')}]$`).test(eventKey)) {
        startValue = eventKey
        shouldSelectAll = false // User is typing, don't select
        // Immediately notify of the initial value
        const numValue = parseFloat(startValue.replace(decimalSeparator, '.'))
        if (!Number.isNaN(numValue)) {
          onValueChange(numValue)
        }
      }
      // Use existing value (double-click, Enter, F2, etc.)
      else if (value !== undefined && value !== null) {
        // Format the value with locale-specific decimal separator
        startValue = String(value).replace('.', decimalSeparator)
        shouldSelectAll = true // Not a numeric key, select all for replacement
      } else {
        startValue = ''
        shouldSelectAll = true // Empty cell, select all (though nothing to select)
      }

      setInternalValue(startValue)

      // Focus and optionally select all text only if this cell started the edit
      if (cellStartedEdit) {
        setTimeout(() => {
          inputRef.current?.focus()
          if (shouldSelectAll) {
            inputRef.current?.select()
          }
        }, 0)
      }
    }, [decimalSeparator, onValueChange])

    // Validation logic
    const getValidationErrors = useCallback(() => {
      if (internalValue === '' || internalValue === '-') {
        return null // Allow empty values and negative sign in progress
      }

      // Normalize: replace locale decimal separator with standard period
      const normalizedValue = internalValue.replace(decimalSeparator, '.')
      const numValue = parseFloat(normalizedValue)

      if (Number.isNaN(numValue)) {
        return ['Please enter a valid number']
      }

      if (!allowNegative && numValue < 0) {
        return ['Negative values are not allowed']
      }

      if (minValue !== undefined && numValue < minValue) {
        return [`Value must be at least ${minValue}`]
      }

      if (maxValue !== undefined && numValue > maxValue) {
        return [`Value must be at most ${maxValue}`]
      }

      return null
    }, [internalValue, allowNegative, minValue, maxValue, decimalSeparator])

    // Register validation with AG Grid
    useGridCellEditor({
      getValidationErrors
    })

    // Handle input change
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value
        // Allow empty string
        if (val === '') {
          setInternalValue('')
          onValueChange(null)
          setHasError(false)
          return
        }

        // Allow negative sign at the beginning
        if (val === '-' && allowNegative) {
          setInternalValue(val)
          setHasError(false)
          return
        }

        // Create locale-aware regex that accepts the locale's decimal separator
        const escapedSeparator = decimalSeparator.replace('.', '\\.')
        const regex = allowDecimals
          ? allowNegative
            ? new RegExp(`^-?\\d*[${escapedSeparator}]?\\d*$`)
            : new RegExp(`^\\d*[${escapedSeparator}]?\\d*$`)
          : allowNegative
          ? /^-?\d*$/
          : /^\d*$/

        if (regex.test(val)) {
          setInternalValue(val)

          // Normalize: replace locale decimal separator with standard period for parsing
          const normalizedVal = val.replace(decimalSeparator, '.')

          // Parse and update value
          const numValue = parseFloat(normalizedVal)
          if (!Number.isNaN(numValue)) {
            onValueChange(numValue)
            const errors = getValidationErrors()
            setHasError(errors !== null)
          } else {
            setHasError(false)
          }
        }
      },
      [onValueChange, allowNegative, allowDecimals, getValidationErrors, decimalSeparator]
    )

    // Handle keyboard events
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Stop editing on Enter
        if (e.key === 'Enter') {
          const errors = getValidationErrors()
          if (errors === null) {
            stopEditing()
          }
        }
        // Cancel editing on Escape
        else if (e.key === 'Escape') {
          stopEditing(true) // Cancel = true
        }
        // Allow navigation keys
        else if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab'].includes(e.key)) {
          // Let the default behavior happen
        }
        // Stop propagation for other keys to prevent grid shortcuts
        else {
          e.stopPropagation()
        }
      },
      [stopEditing, getValidationErrors]
    )

    return (
      <input
        ref={inputRef}
        type="text"
        className={`numeric-cell-editor ${hasError ? 'error' : ''}`}
        value={internalValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        inputMode="decimal"
      />
    )
  }
)

NumericCellEditor.displayName = 'NumericCellEditor'

export default NumericCellEditor
